import type {CellAddress, ColumnKey, ItemId, RowKey} from '../types'
import type {BoardFocusPosition} from './types'

/**
 * What an element drawing a position passes for the half it cannot know.
 *
 * Either paging ghost is one element for the whole board, so neither can say which column
 * or row the reader is in. They pass this, and {@link isSamePosition} ignores that half for
 * those kinds, so the element still matches the position wherever the reader happens to be.
 *
 * **It never reaches a position the board holds.** The reducer resolves every position
 * against the board and fills the carried half in with the real key from wherever the
 * reader already was, so what a caller reads back is always two real coordinates. This is
 * a wildcard on the way in, not a value on the way out.
 */
const CARRIED: string = ''

/** A card in a cell, or the cell itself when `itemId` is `null`. */
export function cellPosition({columnKey, rowKey}: CellAddress, itemId: ItemId | null): BoardFocusPosition {
  return {columnKey, rowKey, itemId, kind: 'cell'}
}

/** The ghost at the end of one cell's cards. */
export function itemPagingPosition({columnKey, rowKey}: CellAddress): BoardFocusPosition {
  return {columnKey, rowKey, itemId: null, kind: 'itemPaging'}
}

/** The ghost past the last column, in the row the reader is in. */
export function columnPagingPosition(columnKey: ColumnKey = CARRIED, rowKey: RowKey = CARRIED): BoardFocusPosition {
  return {columnKey, rowKey, itemId: null, kind: 'columnPaging'}
}

/** The ghost below the last swimlane, in the column the reader is in. */
export function rowPagingPosition(columnKey: ColumnKey = CARRIED, rowKey: RowKey = CARRIED): BoardFocusPosition {
  return {columnKey, rowKey, itemId: null, kind: 'rowPaging'}
}

/**
 * The two axis ghosts as the elements drawing them state them: one of each per board, and
 * neither occupying a coordinate.
 *
 * Constants rather than calls, because a roving element keys its subscription on the
 * object and a fresh one every render would rebuild it.
 */
export const COLUMN_PAGING_POSITION: BoardFocusPosition = columnPagingPosition()

export const ROW_PAGING_POSITION: BoardFocusPosition = rowPagingPosition()

/**
 * Whether two positions are the same place, which is a question about the coordinate each
 * one occupies rather than about every field it holds.
 *
 * Either paging ghost is at neither half, because a board draws one of each and it is the
 * same place however the reader got to it. The anchor is compared separately by the reducer, so a carried half
 * changing is still a change the state notices.
 */
export function isSamePosition(a: BoardFocusPosition | null, b: BoardFocusPosition | null): boolean {
  if (a === null || b === null) return a === b
  if (a.kind !== b.kind) return false

  switch (a.kind) {
    case 'cell':
      return a.columnKey === b.columnKey && a.rowKey === b.rowKey && a.itemId === b.itemId
    case 'itemPaging':
      return a.columnKey === b.columnKey && a.rowKey === b.rowKey
    case 'columnPaging':
    case 'rowPaging':
      return true
  }
}
